// Card Revealed Component
// Back face of the swipe card showing detailed information

import React from 'react';
import { CardRevealedProps, SwipeAction } from '../../types/swipe';
import {
  CardRevealedContainer,
  CardRevealedHeader,
  CardRevealedPhoto,
  CardRevealedInfo,
  CardRevealedName,
  CardRevealedAge,
  CardRevealedLocation,
  CardBio,
  CardInterests,
  CardInterestsTitle,
  CardInterestsList,
  CardInterestTag,
  CardCompatibility,
  CardCompatibilityTitle,
  CardCompatibilityBar,
  CardCompatibilityFill,
  CardCompatibilityScore,
  CardSocialProof,
  CardSocialProofItem,
  CardSocialProofIcon,
  CardActionButtons,
  CardActionButton,
  CardInfoRowInline
} from '../../styles/Swipe/Swipe.styles';

export const CardRevealed: React.FC<CardRevealedProps> = ({
  revealed,
  user,
  onAction,
  isInteractive,
  isAnimating,
  isVisible = false
}) => {
  
  const [flipCooldown, setFlipCooldown] = React.useState(false);
  const lastVisibilityRef = React.useRef(isVisible);

  // Add cooldown period after flip to prevent accidental button presses
  React.useEffect(() => {
    if (isVisible && !lastVisibilityRef.current) {
      // Card just became visible (flipped to back)
      setFlipCooldown(true);
      
      // Enable buttons after 300ms cooldown (reduced for better UX)
      const timer = setTimeout(() => {
        setFlipCooldown(false);
      }, 300);
      
      return () => clearTimeout(timer);
    }
    lastVisibilityRef.current = isVisible;
  }, [isVisible]);

  const handleAction = (e: React.MouseEvent | React.TouchEvent, action: SwipeAction) => {
    // CRITICAL: Only allow actions when this face is visible (card is flipped)
    if (!isVisible) {
      return;
    }
    
    // Prevent action during cooldown period (prevents accidental presses during flip)
    if (flipCooldown) {
      return;
    }
    
    // Prevent gesture handlers from interfering
    e.stopPropagation();
    e.preventDefault();
    
    if (isInteractive && !isAnimating) {
      onAction(action);
    }
  };

  const formatCompatibility = (score: number): string => {
    if (score >= 90) return 'Excellent match!';
    if (score >= 80) return 'Great match!';
    if (score >= 70) return 'Good match';
    if (score >= 60) return 'Decent match';
    return 'Somewhat compatible';
  };

  return (
    <CardRevealedContainer 
      style={{ 
        cursor: 'default',
        pointerEvents: (isVisible && !flipCooldown) ? 'auto' : 'none',
        visibility: isVisible ? 'visible' : 'hidden',
        opacity: flipCooldown ? 0.7 : 1,
        transition: 'opacity 0.2s ease'
      }}
    >
      
      <CardRevealedHeader>
        <CardRevealedPhoto $photoUrl={user.photos[0]} />
        <CardRevealedInfo>
          <CardRevealedName>
            {user.name}
          </CardRevealedName>
          <CardInfoRowInline>
            <CardRevealedAge>
              {user.age}
            </CardRevealedAge>
            <CardRevealedLocation>
              {user.location}
            </CardRevealedLocation>
          </CardInfoRowInline>
        </CardRevealedInfo>
      </CardRevealedHeader>

      {revealed.bio && (
        <CardBio>
          {revealed.bio}
        </CardBio>
      )}

      {/* {revealed.interests.length > 0 && (
        <CardInterests>
          <CardInterestsTitle>
            Interests
          </CardInterestsTitle>
          <CardInterestsList>
            {revealed.interests.map((interest, index) => (
              <CardInterestTag key={index}>
                {interest}
              </CardInterestTag>
            ))}
          </CardInterestsList>
        </CardInterests>
      )} */}

      {revealed.compatibility !== undefined && (
        <CardCompatibility>
          <CardCompatibilityTitle>
            Compatibility
          </CardCompatibilityTitle>
          <CardCompatibilityBar>
            <CardCompatibilityFill $percentage={revealed.compatibility} />
          </CardCompatibilityBar>
          <CardCompatibilityScore>
            {revealed.compatibility}% - {formatCompatibility(revealed.compatibility)}
          </CardCompatibilityScore>
        </CardCompatibility>
      )}

      {revealed.socialProof && (
        <CardSocialProof>
          {revealed.socialProof.mutualFriends > 0 && (
            <CardSocialProofItem>
              <CardSocialProofIcon>👥</CardSocialProofIcon>
              {revealed.socialProof.mutualFriends} mutual friends
            </CardSocialProofItem>
          )}
          {revealed.socialProof.commonInterests > 0 && (
            <CardSocialProofItem>
              <CardSocialProofIcon>🎯</CardSocialProofIcon>
              {revealed.socialProof.commonInterests} common interests
            </CardSocialProofItem>
          )}
        </CardSocialProof>
      )}

      {revealed.personality.length > 0 && (
        <CardInterests>
          <CardInterestsTitle>
            Personality
          </CardInterestsTitle>
          <CardInterestsList>
            {revealed.personality.map((trait, index) => (
              <CardInterestTag key={index}>
                {trait}
              </CardInterestTag>
            ))}
          </CardInterestsList>
        </CardInterests>
      )}

      <CardActionButtons style={{ position: 'relative' }}>
        {flipCooldown && (
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(255, 255, 255, 0.8)',
            zIndex: 100,
            pointerEvents: 'all',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '0.75rem',
            color: '#666',
            fontWeight: 600,
            borderRadius: '12px'
          }}>
            Loading...
          </div>
        )}
        <CardActionButton
          $variant="pass"
          $disabled={!isVisible || !isInteractive || isAnimating || flipCooldown}
          onClick={(e) => handleAction(e, 'pass')}
          onTouchEnd={(e) => handleAction(e, 'pass')}
          style={{ pointerEvents: flipCooldown ? 'none' : 'auto' }}
        >
          Pass
        </CardActionButton>
        <CardActionButton
          $variant="save"
          $disabled={!isVisible || !isInteractive || isAnimating || flipCooldown}
          onClick={(e) => handleAction(e, 'save')}
          onTouchEnd={(e) => handleAction(e, 'save')}
          style={{ pointerEvents: flipCooldown ? 'none' : 'auto' }}
        >
          Keep
        </CardActionButton>
      </CardActionButtons>
    </CardRevealedContainer>
  );
};
